import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  uploadBasename,
  uploadFileAcceptAttribute,
  uploadSourceLabel,
  type UploadMediaMode,
} from "../../utils/uploadFileUtils";
import { VideoUploadFileGrid } from "./VideoUploadFileGrid";

export type DocumentUploadSectionProps = {
  uploadMediaMode: UploadMediaMode;
  files: File[];
  busy: boolean;
  fileInputRef: RefObject<HTMLInputElement>;
  folderInputRef: RefObject<HTMLInputElement>;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileChange: React.ChangeEventHandler<HTMLInputElement>;
  onFolderChange: React.ChangeEventHandler<HTMLInputElement>;
  onRemoveFile: (index: number) => void;
  onSelectFolder: () => void;
  variant?: "full" | "summary" | "bar";
  /** When true (summary only), chips can be removed — used on step 0 right panel. */
  removable?: boolean;
  onEditFiles?: () => void;
};

export function DocumentUploadSection({
  uploadMediaMode,
  files,
  busy,
  fileInputRef,
  folderInputRef,
  onDragOver,
  onDrop,
  onFileChange,
  onFolderChange,
  onRemoveFile,
  onSelectFolder,
  variant = "full",
  removable = false,
  onEditFiles,
}: DocumentUploadSectionProps) {
  const { t } = useTranslation();
  const isSummary = variant === "summary";
  const isBar = variant === "bar";
  const isVideo = uploadMediaMode === "video";
  const fileInputId = isVideo ? "dm-video-file-upload" : "dm-file-upload";
  const accept = uploadFileAcceptAttribute(uploadMediaMode);

  const renderSelectedFiles = (opts: { deletable: boolean }) => {
    if (isVideo && files.length > 0) {
      return (
        <VideoUploadFileGrid
          files={files}
          busy={busy}
          deletable={opts.deletable}
          onRemoveFile={onRemoveFile}
          compact={isBar}
        />
      );
    }
    return renderFileChips(opts);
  };

  const renderFileChips = (opts: { deletable: boolean }) => (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={opts.deletable ? 1 : 0.75}
      className={opts.deletable ? "dm-upload-selected-chips" : undefined}
    >
      {files.map((f, idx) => {
        const base = uploadBasename(f);
        const source = uploadSourceLabel(f);
        return (
          <Chip
            key={`${base}-${f.size}-${f.lastModified}-${idx}`}
            color={opts.deletable ? "primary" : "default"}
            size="small"
            label={base}
            title={source !== base ? `${base} ← ${source}` : base}
            sx={{
              minWidth: opts.deletable ? "auto" : undefined,
              maxWidth: opts.deletable ? 280 : 220,
            }}
            icon={
              opts.deletable && busy ? (
                <CircularProgress size={14} color="inherit" sx={{ ml: 0.5 }} />
              ) : undefined
            }
            onDelete={
              opts.deletable && !busy ? () => onRemoveFile(idx) : undefined
            }
          />
        );
      })}
    </Stack>
  );

  if (isBar) {
    return (
      <Box className="dm-upload-files-bar">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: files.length > 0 ? 1 : 0 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t("opva.dmUploadFilesSelected", {
              defaultValue: "{{count}} file(s) selected",
              count: files.length,
            })}
          </Typography>
          {onEditFiles ? (
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={onEditFiles}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              {t("opva.dmUploadEditFiles", { defaultValue: "Change files" })}
            </Button>
          ) : null}
        </Stack>
        {files.length > 0 ? (
          <Box
            className={
              isVideo
                ? "dm-upload-files-bar__videos"
                : "dm-upload-files-bar__chips"
            }
          >
            {renderSelectedFiles({ deletable: false })}
          </Box>
        ) : null}
      </Box>
    );
  }

  if (isSummary) {
    return (
      <Box className="dm-upload-summary">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t("opva.dmUploadFilesSelected", {
              defaultValue: "{{count}} file(s) selected",
              count: files.length,
            })}
          </Typography>
          {onEditFiles ? (
            <Button
              type="button"
              size="small"
              variant="text"
              disabled={busy}
              onClick={onEditFiles}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              {t("opva.dmUploadEditFiles", { defaultValue: "Change files" })}
            </Button>
          ) : null}
        </Stack>
        {files.length > 0 ? (
          renderSelectedFiles({ deletable: removable })
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("opva.dmUploadNoFiles", {
              defaultValue: "No files selected.",
            })}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <>
      <div
        className={`upload-box-container dm-upload-dropzone${busy ? " dm-upload-dropzone--busy" : ""}`}
        onDragOver={busy ? undefined : onDragOver}
        onDrop={busy ? undefined : onDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          id={fileInputId}
          accept={accept}
          onChange={onFileChange}
        />
        <div className="dm-upload-primary-block">
          <div className="d-flex align-self-center justify-content-center mb-3">
            <label htmlFor={fileInputId} className="file-upload-icon">
              <FileUploadOutlinedIcon color="primary" sx={{ fontSize: 40 }} />
            </label>
          </div>
          <div className="d-flex align-self-center justify-content-center mb-2 px-2 text-center">
            <label htmlFor={fileInputId} className="dm-upload-click-label">
              <Link variant="bodyNormal" underline="none" className="me-1">
                <b>{t("opva.clickHere")}</b>
              </Link>
              <Typography variant="bodyNormal" component="span">
                {t("opva.uploadDropMedia")}
              </Typography>
            </label>
          </div>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{
              maxWidth: 480,
              lineHeight: 1.5,
              minHeight: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isVideo
              ? t("opva.dmUploadVideoFormatsHint", {
                  defaultValue:
                    "Upload MP4, MOV, AVI, MKV, or WebM files (up to 10 MB each).",
                })
              : t("opva.dmUploadFormatsHint", {
                  defaultValue:
                    "Select one or more files, or drag them into this area.",
                })}
          </Typography>
        </div>
      </div>

      <Box
        className="dm-upload-folder-secondary"
        aria-hidden={isVideo}
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          maxWidth: "100%",
          overflowX: "auto",
          py: 0.5,
          // Reserve the same vertical space in Videos mode to avoid layout jump.
          visibility: isVideo ? "hidden" : "visible",
          pointerEvents: isVideo ? "none" : "auto",
        }}
      >
        <input
          ref={folderInputRef}
          type="file"
          multiple
          hidden
          id="dm-folder-upload"
          onChange={onFolderChange}
          tabIndex={isVideo ? -1 : undefined}
          {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          component="span"
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          {t("opva.dmFolderAdditionalLead", {
            defaultValue: "You can also",
          })}
        </Typography>
        <Button
          type="button"
          variant="outlined"
          size="small"
          disabled={busy || isVideo}
          onClick={onSelectFolder}
          tabIndex={isVideo ? -1 : undefined}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            whiteSpace: "nowrap",
            flexShrink: 0,
            px: 1.5,
            borderColor: "divider",
          }}
        >
          {t("opva.dmSelectFolder", { defaultValue: "Select folder" })}
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
          component="span"
          sx={{ lineHeight: 1.4, whiteSpace: "nowrap" }}
        >
          {t("opva.dmFolderAdditionalTail", {
            defaultValue:
              "to add every file from that folder (directory upload in modern browsers).",
          })}
        </Typography>
      </Box>
    </>
  );
}
